import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import classNames from 'classnames'
import { random } from 'lodash'

import {
    KEY_CODES, LOCAL_STORAGE_KEYS, REACTIVE_CLAN_MESSAGE_TYPE } from 'constants'

import {
    CLAN_LANDING_ROLE_DESCRIPTION,
    CLAN_SUPPLY_PROGRESSION_DISABLED_TEXT,
    CLAN_SUPPLY_QUESTS_DISABLED_TEXT,
    CLAN_SUPPLY_QUESTS_TITLE,
    CLAN_SUPPLY_PROGRESSION_TITLE,
} from './translations'

import { requestClanLanding } from 'state/actions/clan_landing'

import { openHangar, Tooltip } from 'react-wot-bootstrap'
import Clan from 'components/Clan/Clan'
import LoadingScreen from 'components/LoadingScreen/LoadingScreen'

import MainCard from './components/MainCard'
import StrongholdCard from './components/StrongholdCard'
import GlobalMapCard from './components/GlobalMapCard'
import ManeuversCard from './components/ManeuversCard'
import AnnouncementsCard from './components/AnnouncementsCard/AnnouncementsCard'
import ClanLandingReserves from './components/ClanLandingReserves/ClanLandingReserves'
import ClanSupplyQuestsCard from './components/ClanSupplyQuestsCard'
import ClanSupplyProgressionCard from './components/ClanSupplyProgressionCard'
import DisabledCard from './components/DisabledCard/DisabledCard'

import { getLocalizedName } from 'utils/clan_management/roles'
import { subscribeToChannel, unsubscribeFromChannel, parseHermodJSONData } from 'helpers/hermod'
import { sendEnterLandingSoundState } from 'state/actions/sounds'
import { requestBalance } from 'state/actions/balance'
import { addWOTClientListener, C2W_EVENT_TYPES, removeWOTClientListener } from '@wotfe/client2web'

import styles from './ClanLanding.scss'
import cardStyles from './components/cards.scss'
import { requestAnnouncements } from '../../state/actions/announcements'
import { requestGlobalMapInfo } from '../../state/actions/global_map'
import { requestCraftMachineInfo } from '../../state/actions/craft_machine'
import { requestNextChallengeInfo } from '../../state/actions/challenges'
import { requestNews } from '../../state/actions/webbrg'
import { requestShopStatus } from '../../state/actions/shop'
import { requestClanSupplyQuestsInfo } from '../../state/actions/clan_supply_quests'
import { requestServerTimestamp } from '../../state/actions/client'
import { requestClientCounters } from '../../state/actions/client_counters'
import { requestStrongholdManeuversInfo } from '../../state/actions/stronghold_maneuvers'
import { getParsedStorageItem, saveToStorage } from 'helpers/playersMatching'
import ClanQuestsTutorial from 'containers/ClanLanding/components/ClanQuestsTutorial/ClanQuestsTutorialConnector'
import { requestClanSupplyProgressionMainInfo } from '../../state/actions/clan_supply_progression_main'

import { selectQuests } from './selectors'

const CLAN_TOOLTIP_ID = 'clan_tooltip'
const HIDE_INDICATOR_TIMEOUT = 5000

class ClanLanding extends React.Component {
    static propTypes = {
        accountRole: PropTypes.string,
        clan: PropTypes.object.isRequired,
        clanChannelId: PropTypes.string.isRequired,
        client: PropTypes.object.isRequired,
        clientCounters: PropTypes.object.isRequired,
        showAnnouncements: PropTypes.bool,
        globalMapDisabled: PropTypes.bool,
        showClanQuestsCard: PropTypes.bool,
        loading: PropTypes.bool,
        industrialResourceMultiplier: PropTypes.number.isRequired,

        loadClanLanding: PropTypes.func.isRequired,
        requestGlobalMapInfo: PropTypes.func.isRequired,
        sendEnterLandingSoundState: PropTypes.func.isRequired,
        isManeuversEventVisible: PropTypes.bool.isRequired,
        requestStrongholdManeuversInfo: PropTypes.func.isRequired,
        wgshEventsUrl: PropTypes.string.isRequired,
        areQuestsEnabled: PropTypes.bool,
        isMapSettingsEnabled: PropTypes.bool,
        onComponentDidMount: PropTypes.func.isRequired,
        onComponentWillUnmount: PropTypes.func.isRequired,
        balance: PropTypes.shape({
            tourcoin: PropTypes.number.isRequired,
        }),
    }

    constructor(props) {
        super(props)

        this.state = {
            hideClanQuestIntro: true,
            showReactiveIndicator: false,
        }
    }

    isTutorialRequired = () => {
        return this.props.showClanQuestsCard &&
            !this.state.hideClanQuestIntro &&
            !getParsedStorageItem(LOCAL_STORAGE_KEYS.CLAN_QUESTS_INTRO_SHOWN)
    }

    componentDidMount() {
        this.props.onComponentDidMount()
        this.props.loadClanLanding(this.props)
        window.addEventListener('keydown', this.onKeyDown)
        window.onBeforeUnloadHandlers.unsubscribeChannelOnExit = () => {
            this.unsubscribeChannel()
        }
        this.props.sendEnterLandingSoundState()

        this.props.requestGlobalMapInfo()
        this.props.requestStrongholdManeuversInfo()
        this.subscribeChannel()
    }

    componentWillUnmount() {
        this.props.onComponentWillUnmount()
        window.removeEventListener('keydown', this.onKeyDown)
        delete window.onBeforeUnloadHandlers.unsubscribeChannelOnExit

        this.resetGlobalMapTimer()
        this.resetReactiveIndicatorTimer()
        this.unsubscribeChannel()
    }

    onKeyDown = (e) => {
        if (e.keyCode === KEY_CODES.ESCAPE) {
            openHangar()
        }
    }

    subscribeChannel() {
        subscribeToChannel(this.props.clanChannelId, this.handleReactiveChannelMessage)
    }

    unsubscribeChannel() {
        unsubscribeFromChannel(this.props.clanChannelId, this.handleReactiveChannelMessage)
    }

    resetGlobalMapTimer = () => {
        if (this.globalMapTimerId) {
            clearTimeout(this.globalMapTimerId)
        }
    }

    resetReactiveIndicatorTimer = () => {
        if (this.reactiveIndicatorTimerId) {
            clearTimeout(this.reactiveIndicatorTimerId)
        }
    }

    handleReactiveChannelMessage = (c2wEvent) => {
        const data = parseHermodJSONData(c2wEvent)
        const isBattlesUpdated = data && data.type === REACTIVE_CLAN_MESSAGE_TYPE.BATTLES_UPDATED
        const isPingEvent = data && data.type === REACTIVE_CLAN_MESSAGE_TYPE.PING

        if (isBattlesUpdated) {
            const timeout = random(2000, 6000)

            this.resetGlobalMapTimer()
            this.globalMapTimerId = setTimeout(() => {
                this.props.requestGlobalMapInfo(true)
            }, timeout)
        }

        if (isPingEvent) {
            this.resetReactiveIndicatorTimer()
            this.setState({ showReactiveIndicator: true })
            this.reactiveIndicatorTimerId = setTimeout(
                () => this.setState({ showReactiveIndicator: false }),
                HIDE_INDICATOR_TIMEOUT,
            )
        }
    }

    handleClanQuestIntroClose = () => {
        saveToStorage(LOCAL_STORAGE_KEYS.CLAN_QUESTS_INTRO_SHOWN, true)
        this.setState({ hideClanQuestIntro: true })
    }

    renderHeader() {
        const emblem = this.props.clan.emblem.replace('32x32', '100x100_wgsh')

        return (
            <div className={styles.header_wrapper}>
                <header className={styles.header}>
                    <img
                        className={styles.clan_logo}
                        src={emblem}
                        data-tip data-for={CLAN_TOOLTIP_ID}
                    />
                    <h1>
                        <span data-tip data-for={CLAN_TOOLTIP_ID}>
                            <span
                                className={styles.clan_tag}
                                style={{ color: this.props.clan.color }}
                            >
                                {`[${this.props.clan.tag}]`}
                            </span>
                            &nbsp;
                            {this.props.clan.name}
                        </span>
                    </h1>
                    {this.props.accountRole && (
                        <small>
                            {CLAN_LANDING_ROLE_DESCRIPTION}
                            &nbsp;
                            {getLocalizedName(this.props.accountRole)}
                        </small>
                    )}
                    <Tooltip id={CLAN_TOOLTIP_ID}>
                        <Clan {...this.props.clan} />
                    </Tooltip>
                </header>
                <ClanLandingReserves />
            </div>
        )
    }

    renderClanQuestsIntro() {
        if (!this.isTutorialRequired()) {
            return null
        }

        return (
            <ClanQuestsTutorial onClose={this.handleClanQuestIntroClose} />
        )
    }

    renderMainCard() {
        return (
            <div className={classNames(styles.main_card, styles.card)}>
                <MainCard withQuests={this.props.showClanQuestsCard} />
                <div className={styles.card__hover_overlay} />
            </div>
        )
    }

    renderAnnouncementsCard() {
        if (!this.props.showAnnouncements) {
            return null
        }

        const announcementsCardClassName = classNames(styles.announcements_card, styles.card)

        return (
            <div className={announcementsCardClassName} >
                <AnnouncementsCard withQuests={this.props.showClanQuestsCard} />
                <div className={styles.card__hover_overlay} />
            </div>
        )
    }

    renderStrongholdCard() {
        const {
            industrialResourceMultiplier,
            clientCounters,
            showAnnouncements,
            showClanQuestsCard,
        } = this.props

        return (
            <div className={classNames(
                styles.card,
                styles.stronghold_card,
                industrialResourceMultiplier > 1 && styles.card__multiplier,
            )}
            >
                <StrongholdCard
                    withNews={showAnnouncements}
                    withQuests={showClanQuestsCard}
                    withTutorialArrow={clientCounters.items.clanNews > 0}
                />
                <div className={styles.card__hover_overlay} />
            </div>
        )
    }

    renderGlobalMapCard() {
        if (this.props.isManeuversEventVisible) {
            return null
        }

        const globalMapCardClassName = classNames(
            styles.globalmap_card,
            styles.card,
            this.props.globalMapDisabled && styles.card__disabled
        )

        return (
            <div className={globalMapCardClassName}>
                {this.state.showReactiveIndicator && (
                    <div className={styles.reactive_indicator} />
                )}
                <GlobalMapCard withNews={this.props.showAnnouncements} withQuests={this.props.showClanQuestsCard} />
                <div className={styles.card__hover_overlay} />
            </div>
        )
    }

    renderManeuversCard() {
        const {
            showAnnouncements,
            showClanQuestsCard,
            wgshEventsUrl,
        } = this.props

        const maneuversCardClassName = classNames(
            styles.maneuvers_card,
            styles.card,
        )

        return (
            <div className={maneuversCardClassName}>
                <ManeuversCard
                    withNews={showAnnouncements}
                    withQuests={showClanQuestsCard}
                    wgshEventsUrl={wgshEventsUrl}
                />
                <div className={styles.card__hover_overlay} />
            </div>
        )
    }

    renderClanSupply() {
        return (
            <React.Fragment>
                <div className={classNames(
                    styles.clanSupplyQuests_card,
                    styles.card,
                    !this.props.areQuestsEnabled && styles.card__disabled,
                )}
                >
                    <div className={classNames(cardStyles.clanSupplyCard, cardStyles.clanSupplyCard__quests)}>
                        {this.props.areQuestsEnabled ?
                            <ClanSupplyQuestsCard /> :
                            <DisabledCard
                                title={CLAN_SUPPLY_QUESTS_TITLE}
                                text={CLAN_SUPPLY_QUESTS_DISABLED_TEXT}
                            />
                        }
                        <div className={styles.card__hover_overlay} />
                    </div>
                </div>
                <div className={classNames(
                    styles.clanSupplyProgression_card,
                    styles.card,
                    !this.props.isMapSettingsEnabled && styles.card__disabled,
                )}
                >
                    <div className={classNames(cardStyles.clanSupplyCard, cardStyles.clanSupplyCard__progression)}>
                        {this.props.isMapSettingsEnabled ?
                            <ClanSupplyProgressionCard tourCoin={this.props.balance.tourcoin} /> :
                            <DisabledCard
                                title={CLAN_SUPPLY_PROGRESSION_TITLE}
                                text={CLAN_SUPPLY_PROGRESSION_DISABLED_TEXT}
                            />
                        }
                    </div>
                    <div className={styles.card__hover_overlay} />
                </div>
            </React.Fragment>
        )
    }

    getGridModifier() {
        const {
            showAnnouncements,
            showClanQuestsCard,
            isManeuversEventVisible,
        } = this.props

        return showAnnouncements ?
        (
          showClanQuestsCard ?
            (
              isManeuversEventVisible ?
                styles.grid__with_announcements_and_clansupply_and_maneuvers :
                styles.grid__with_announcements_and_clansupply
            ) :
            (
              isManeuversEventVisible ?
                styles.grid__with_announcements_and_maneuvers :
                styles.grid__with_announcements
            )
        ) :
        (
          showClanQuestsCard ?
            (
              isManeuversEventVisible ?
                styles.grid__with_clansupply_and_maneuvers :
                styles.grid__with_clansupply
            ) :
            (
              isManeuversEventVisible ?
                styles.grid__with_maneuvers :
                null
            )
        )
    }

    render() {
        if (this.props.loading) {
            return <LoadingScreen type='fullscreen' />
        }

        const gridClassName = classNames(
            styles.grid,
            this.getGridModifier(),
        )

        return (
            <div className={styles.base}>
                <div
                    className={classNames(styles.wrapper, {
                        [styles.wrapper__blurred]: this.isTutorialRequired(),
                    })}
                >
                    {this.renderHeader()}
                    <div className={gridClassName}>
                        {this.renderMainCard()}
                        {this.renderAnnouncementsCard()}
                        {this.renderStrongholdCard()}
                        {this.renderGlobalMapCard()}
                        {this.renderManeuversCard()}
                        {this.props.showClanQuestsCard && (
                            this.renderClanSupply()
                        )}
                    </div>
                </div>
                {this.renderClanQuestsIntro()}
            </div>
        )
    }
}

export const isClanSupplyQuestsLoading = ({
    clanSupplyQuests,
    client,
}) => {
    const clanSupplyQuestsDataLoadingFirstTime = clanSupplyQuests.loading &&
        !(clanSupplyQuests.loaded || clanSupplyQuests.errorMessage)
    const clientProductsLoading = client.loading

    return clanSupplyQuestsDataLoadingFirstTime || clientProductsLoading
}

export const isClanSupplyProgressionSettingsLoading = ({
   clanSupplyProgressionSettings,
   client,
}) => {
    const dataLoadingFirstTime = clanSupplyProgressionSettings.loading &&
        !(clanSupplyProgressionSettings.loaded || clanSupplyProgressionSettings.errorMessage)

    const clientProductsLoading = client.loading

    return dataLoadingFirstTime || clientProductsLoading
}

export const isClanSupplyProgressionLoading = ({
     clanSupplyProgression,
     client,
}) => {
    const dataLoadingFirstTime = clanSupplyProgression.loading &&
        !(clanSupplyProgression.loaded || clanSupplyProgression.errorMessage)

    const clientProductsLoading = client.loading

    return dataLoadingFirstTime || clientProductsLoading
}

/* eslint-disable complexity  */
export const stateToProps = (state) => {
    return {
        clan: state.clan,
        clanChannelId: `gm_clan_${state.clan.id}`,
        client: state.client,
        clientCounters: state.clientCounters,
        accountRole: state.account.role,
        globalMapDisabled: !state.globalMap.context_id || !state.settings.globalMapUrl,
        showClanQuestsCard: state.settings.showClanQuestsCard,
        wgshEventsUrl: state.settings.wgshEventsUrl,
        industrialResourceMultiplier: state.settings.industrialResourceMultiplier,
        showAnnouncements: (
             state.announcements.list.length > 0 ||
             state.webbrg.clanNews.length > 0 ||
             state.announcements.settings.can_create_messages
         ),
        loading: (
            state.announcements.isLoading ||
            !state.stronghold.level ||
            // we have reactive updates from a server for the global map object
            // so loading screen should be shown only on initial loading
            (!state.globalMap.loaded && state.globalMap.loading) ||
            state.webbrg.loading ||
            (!state.shop.statusLoaded && !state.shop.errorMessage) ||
            (!state.clientCounters.loaded && state.clientCounters.loading) ||
            isClanSupplyQuestsLoading(state) ||
            isClanSupplyProgressionSettingsLoading(state) ||
            isClanSupplyProgressionLoading(state)
        ),
        areQuestsEnabled: (state.clanSupplyQuests && state.clanSupplyQuests.enabled) ?
            selectQuests(state).enabled : false,
        isMapSettingsEnabled: (state.clanSupplyProgressionSettings && state.clanSupplyProgression) ?
            (state.clanSupplyProgressionSettings.enabled && !state.clanSupplyProgression.errorMessage) :
            false,
        balance: state.balance,
    }
}

export const dispatchToProps = (dispatch) => ({
    onComponentDidMount: () => {
        dispatch(requestBalance())
        addWOTClientListener(C2W_EVENT_TYPES.UPDATE_BALANCE, dispatch)
        addWOTClientListener(C2W_EVENT_TYPES.WALLET_UPDATE, dispatch)
    },
    onComponentWillUnmount: () => {
        removeWOTClientListener(C2W_EVENT_TYPES.UPDATE_BALANCE, dispatch)
        removeWOTClientListener(C2W_EVENT_TYPES.WALLET_UPDATE, dispatch)
    },
    loadClanLanding: ({ showClanQuestsCard }) => {
        dispatch(requestAnnouncements())
        dispatch(requestCraftMachineInfo())
        dispatch(requestNextChallengeInfo())
        dispatch(requestClanLanding())
        dispatch(requestNews())
        dispatch(requestShopStatus())
        dispatch(requestClientCounters())
        dispatch(requestServerTimestamp())
        if (showClanQuestsCard) {
            dispatch(requestClanSupplyQuestsInfo())
            dispatch(requestClanSupplyProgressionMainInfo())
        }
    },
    requestGlobalMapInfo: (notResetState) => dispatch(requestGlobalMapInfo(notResetState)),
    sendEnterLandingSoundState: () => dispatch(sendEnterLandingSoundState()),
    requestStrongholdManeuversInfo: () => dispatch(requestStrongholdManeuversInfo()),
})

export default connect(stateToProps, dispatchToProps)(ClanLanding)



// WEBPACK FOOTER //
// ./containers/ClanLanding/ClanLanding.jsx